import {
  buildFederatedPhotoTreatmentUrl,
  extractFederatedPhotoInfoFromUrl,
} from '@/src/components/FederatedPhoto/federatedPhoto';

type SizedPhoto = {
  url?: string;
  caption?: string;
  thumb?: { url?: string };
  squareThumb?: { url?: string };
  medium?: { url?: string };
  large?: { url?: string };
};

export type FloorplanMedia = {
  layouts?: Array<SizedPhoto>;
  largePhotos?: Array<SizedPhoto>;
  photos?: Array<SizedPhoto>;
  thumbPhotos?: Array<SizedPhoto>;
  listingPhotos?: Array<SizedPhoto>;
  layoutCount?: number;
};

export type FloorplanPhoto = {
  url: string;
  caption?: string;
};

function pickPhotoUrl(photo: SizedPhoto | undefined): string | null {
  if (!photo) return null;
  return photo.large?.url ?? photo.medium?.url ?? photo.url ?? photo.thumb?.url ?? photo.squareThumb?.url ?? null;
}

const LARGE_RENDER_RE = /-rentals_large_\d+_\d+\.(webp|jpg)$/;
const CARD_RENDER_TREATMENT = 'rentals_medium_500_500';

/**
 * Floorplan `layouts` entries often carry a bare url already baked as a
 * rentals_large render (~57KB); the medium render of the same photo key
 * (~16KB) is what the ~350-400 CSS px card actually needs. Swap the treatment
 * when the URL is a Federated Photos render; anything else passes through.
 */
function toCardRenderUrl(url: string): string {
  if (!LARGE_RENDER_RE.test(url)) return url;
  const info = extractFederatedPhotoInfoFromUrl(url);
  if (!info) return url;
  return buildFederatedPhotoTreatmentUrl(info.photoKey, CARD_RENDER_TREATMENT, {
    origin: info.origin,
    ext: url.endsWith('.jpg') ? 'jpg' : 'webp',
  });
}

/**
 * Card-surface variant: prefer the medium render (~500px) over large
 * (~1200px). Floor plan cards display at ~350-400 CSS px, so large is ~4x the
 * bytes for no visible gain; lightbox-class surfaces keep the large-first
 * pickPhotoUrl order.
 */
function pickCardPhotoUrl(photo: SizedPhoto | undefined): string | null {
  if (!photo) return null;
  const url = photo.medium?.url ?? photo.large?.url ?? photo.url ?? photo.thumb?.url ?? photo.squareThumb?.url ?? null;
  return url ? toCardRenderUrl(url) : null;
}

function normalizePhoto(photo: SizedPhoto | undefined): FloorplanPhoto | null {
  const url = pickPhotoUrl(photo);
  if (!url) return null;
  return { url, caption: photo?.caption };
}

function firstPhotoUrl(photos: Array<SizedPhoto> | undefined): string | null {
  for (const photo of photos ?? []) {
    const url = pickPhotoUrl(photo);
    if (url) return url;
  }
  return null;
}

function firstCardPhotoUrl(photos: Array<SizedPhoto> | undefined): string | null {
  for (const photo of photos ?? []) {
    const url = pickCardPhotoUrl(photo);
    if (url) return url;
  }
  return null;
}

export function getFloorplanLayoutCount(floorplan: FloorplanMedia): number {
  return floorplan.layoutCount ?? floorplan.layouts?.length ?? 0;
}

export function getFloorplanImageUrl(floorplan: FloorplanMedia): string | null {
  return (
    firstPhotoUrl(floorplan.layouts) ??
    firstPhotoUrl(floorplan.largePhotos) ??
    firstPhotoUrl(floorplan.photos) ??
    firstPhotoUrl(floorplan.listingPhotos) ??
    firstPhotoUrl(floorplan.thumbPhotos)
  );
}

export function getFloorplanCardImageUrl(floorplan: FloorplanMedia): string | null {
  return (
    firstCardPhotoUrl(floorplan.layouts) ??
    firstCardPhotoUrl(floorplan.largePhotos) ??
    firstCardPhotoUrl(floorplan.photos) ??
    firstCardPhotoUrl(floorplan.listingPhotos) ??
    firstCardPhotoUrl(floorplan.thumbPhotos)
  );
}

export function getFloorplanInteriorPhotos(floorplan: FloorplanMedia): Array<FloorplanPhoto> {
  const layoutCount = floorplan.layoutCount ?? 0;
  const photos = floorplan.largePhotos?.length ? floorplan.largePhotos : floorplan.photos;

  if (!photos?.length || layoutCount === 0) {
    return [];
  }

  return photos
    .slice(layoutCount)
    .map(normalizePhoto)
    .filter((photo): photo is FloorplanPhoto => photo !== null);
}

export function getUnitInteriorPhotos(unit: FloorplanMedia, floorplan: FloorplanMedia): Array<FloorplanPhoto> {
  const photos = unit.largePhotos?.length ? unit.largePhotos : unit.photos;
  const unitPhotos = (photos ?? []).map(normalizePhoto).filter((photo): photo is FloorplanPhoto => photo !== null);

  return unitPhotos.length ? unitPhotos : getFloorplanInteriorPhotos(floorplan);
}
